// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {showPremiumActionErrorModal} from '@app/features/app/components/dialogs/components/plutonium/utils/PremiumActionErrorModal';
import * as BoostSubscriptionCommands from '@app/features/boost/commands/BoostSubscriptionCommands';
import {BoostGemIcon} from '@app/features/boost/components/BoostGemIcon';
import {BoostSubscriptionModal} from '@app/features/boost/components/BoostSubscriptionModal';
import styles from '@app/features/boost/components/BoostSubscriptionRow.module.css';
import BoostSubscription from '@app/features/boost/state/BoostSubscription';
import {boostSubscriptionRoute} from '@app/features/boost/utils/GuildBoostPurchase';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import {OPEN_PAYMENT_PAGE_DESCRIPTOR} from '@app/features/shop/utils/ShopMessageDescriptors';
import {formatPayable} from '@app/features/shop/utils/ShopPriceFormat';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {isDesktop, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import WindowState from '@app/features/window/state/Window';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {getFormattedLongDate} from '@fluxer/date_utils/src/DateFormatting';
import type {PaddleBoostSubscriptionState} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {reaction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Boost subscription',
	comment: 'Heading of the monthly boost subscription row on the Boost page of a community.',
});
const COUNT_DESCRIPTOR = msg({
	message: '{count, plural, one {# boost} other {# boosts}}',
	comment: 'How many boosts the monthly boost subscription holds, in its row on the Boost page.',
});
const NEXT_PAYMENT_DESCRIPTOR = msg({
	message: 'next payment {date}',
	comment: 'Part of the boost subscription row, after the number of boosts. {date} is a long date.',
});
const ENDS_ON_DESCRIPTOR = msg({
	message: 'ends on {date}',
	comment: 'Part of the boost subscription row after a cancellation. {date} is a long date.',
});
const PER_MONTH_DESCRIPTOR = msg({
	message: '{amount} / mo',
	comment: 'The monthly total of the boost subscription in its row, e.g. "₺249,95 / mo".',
});
const PAYMENT_PENDING_DESCRIPTOR = msg({
	message: 'Payment pending',
	comment: 'Status of a renewing subscription whose first payment is not confirmed yet.',
});
const PAST_DUE_DESCRIPTOR = msg({
	message: "We couldn't take the last payment. Update your payment method to keep your boosts.",
	comment: 'Note in the boost subscription row when a renewal payment failed.',
});
const NEEDS_REVIEW_DESCRIPTOR = msg({
	message: 'We are checking this subscription. If it does not clear up soon, contact support.',
	comment: 'Note in the subscription box when the subscription needs a manual check.',
});
const MANAGE_DESCRIPTOR = msg({
	message: 'Manage',
	comment: 'Button in the boost subscription row; opens the dialog that changes the number of boosts.',
});
const CANCEL_DESCRIPTOR = msg({
	message: 'Cancel subscription',
	comment: 'Button in the subscription box and in its confirm dialog; stops future renewals.',
});
const KEEP_DESCRIPTOR = msg({
	message: 'Keep subscription',
	comment: 'Button in the cancel confirm dialog that closes it.',
});
const REACTIVATE_DESCRIPTOR = msg({
	message: 'Undo cancellation',
	comment: 'Button in the subscription box after a cancellation; the subscription renews again.',
});
const PAYMENT_METHOD_DESCRIPTOR = msg({
	message: 'Update payment method',
	comment: 'Button in the subscription box; opens the payment page of Paddle in the browser.',
});
const CONFIRM_TITLE_DESCRIPTOR = msg({
	message: 'Cancel automatic renewal?',
	comment: 'Title of the confirm dialog before cancelling a renewing subscription.',
});
const CONFIRM_BODY_DESCRIPTOR = msg({
	message: 'You will not be charged again. Your boosts stay until the end of the time you have paid for ({date}).',
	comment: 'Body of the confirm dialog before cancelling the boost subscription. {date} is the end of the paid period.',
});
const CANCELLED_DESCRIPTOR = msg({
	message: 'Your boost subscription is canceled. Your boosts stay until {date}.',
	comment: 'Toast after the server confirmed the cancellation of the boost subscription. {date} is a long date.',
});
const REACTIVATED_DESCRIPTOR = msg({
	message: 'Your subscription renews again.',
	comment: 'Toast after the server confirmed that a cancellation was undone.',
});
const PENDING_DESCRIPTOR = msg({
	message: 'Your request was received. The status will update shortly.',
	comment: 'Toast after a subscription change was sent but the server does not show it yet.',
});
const CANCEL_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't cancel your subscription",
	comment: 'Title of the error dialog when cancelling a renewing subscription fails.',
});
const REACTIVATE_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't undo the cancellation",
	comment: 'Title of the error dialog when undoing the cancellation of a renewing subscription fails.',
});
const PAYMENT_METHOD_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't open the payment page",
	comment: 'Title of the error dialog when the payment method page of Paddle cannot be opened.',
});
const TRY_AGAIN_DESCRIPTOR = msg({
	message: 'Something went wrong. Please try again in a moment.',
	comment: 'Body of the error dialogs of the subscription box.',
});
const CHANGE_REFUSED_DESCRIPTOR = msg({
	message: "This can't be changed right now, for example just before a renewal payment. Please try again later.",
	comment: 'Body of the error dialog when Paddle refuses a subscription change at this moment.',
});

type Action = 'cancel' | 'reactivate' | 'payment_method';

function failWith(error: unknown, title: MessageDescriptor, dataFlx: string): void {
	const message =
		failureCode(error) === APIErrorCodes.SHOP_SUBSCRIPTION_CHANGE_REFUSED
			? CHANGE_REFUSED_DESCRIPTOR
			: TRY_AGAIN_DESCRIPTOR;
	showPremiumActionErrorModal(error, {fallbackTitle: title, fallbackMessage: message}, dataFlx);
}

// The account's monthly boost subscription on the Boost page: how many, when it charges next, and its actions. Which
// actions show is the server's call (can_*); success is said only once the server's answer shows the change.
export const BoostSubscriptionRow = observer(() => {
	const {i18n} = useLingui();
	const subscription = BoostSubscription.subscription;
	const [busy, setBusy] = useState(false);
	// On the web the payment page opens from a real link the user clicks, which no pop-up blocker stops.
	const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
	const pageOpened = useRef(false);
	// Back from the Paddle page: read the subscription once, so a changed card or a paid renewal shows.
	useEffect(
		() =>
			reaction(
				() => WindowState.focused && WindowState.visible,
				(back) => {
					if (!back || !pageOpened.current) return;
					pageOpened.current = false;
					void BoostSubscription.load();
				},
			),
		[],
	);
	if (!subscription || boostSubscriptionRoute(subscription, Date.now()) === 'start') return null;

	const date = (iso: string | null) => (iso ? getFormattedLongDate(new Date(iso), i18n.locale) : null);
	// An ended subscription with paid time left ends when that time does.
	const cancelAt = date(
		subscription.cancel_at ?? (subscription.status === 'canceled' ? subscription.paid_until : null),
	);
	const nextPayment = !cancelAt ? date(subscription.next_billed_at) : null;
	const paidUntil = date(subscription.paid_until) ?? '—';
	const parts = [
		i18n._(COUNT_DESCRIPTOR, {count: subscription.quantity}),
		cancelAt
			? i18n._(ENDS_ON_DESCRIPTOR, {date: cancelAt})
			: nextPayment && i18n._(NEXT_PAYMENT_DESCRIPTOR, {date: nextPayment}),
		i18n._(PER_MONTH_DESCRIPTOR, {
			amount: formatPayable(subscription.amount_minor, subscription.currency, i18n.locale),
		}),
	].filter(Boolean);

	const run = async (action: Action): Promise<void> => {
		if (busy) return;
		setBusy(true);
		try {
			if (action === 'payment_method') {
				const url = await BoostSubscriptionCommands.createBoostPaymentMethodUrl();
				if (!url.startsWith('https://')) throw new Error('Unexpected payment page address');
				if (isDesktop()) {
					pageOpened.current = true;
					void openExternalUrl(url);
				} else {
					setPaymentUrl(url);
				}
				return;
			}
			let after: PaddleBoostSubscriptionState;
			try {
				after =
					action === 'cancel'
						? await BoostSubscriptionCommands.cancelBoostSubscription()
						: await BoostSubscriptionCommands.reactivateBoostSubscription();
			} catch (error) {
				failWith(
					error,
					action === 'cancel' ? CANCEL_FAILED_TITLE_DESCRIPTOR : REACTIVATE_FAILED_TITLE_DESCRIPTOR,
					`boost.subscription-row.${action}.error-modal`,
				);
				void BoostSubscription.load();
				return;
			}
			BoostSubscription.set(after);
			const end = date(after.cancel_at);
			if (action === 'cancel' && end) ToastCommands.success(i18n._(CANCELLED_DESCRIPTOR, {date: end}));
			else if (action === 'reactivate' && !after.cancel_at) ToastCommands.success(i18n._(REACTIVATED_DESCRIPTOR));
			else ToastCommands.createToast({type: 'info', children: i18n._(PENDING_DESCRIPTOR)});
		} catch (error) {
			failWith(error, PAYMENT_METHOD_FAILED_TITLE_DESCRIPTOR, 'boost.subscription-row.payment-method.error-modal');
		} finally {
			setBusy(false);
		}
	};

	const confirmCancel = () =>
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(CONFIRM_TITLE_DESCRIPTOR)}
					description={i18n._(CONFIRM_BODY_DESCRIPTOR, {date: paidUntil})}
					primaryText={i18n._(CANCEL_DESCRIPTOR)}
					primaryVariant="danger"
					secondaryText={i18n._(KEEP_DESCRIPTOR)}
					onPrimary={() => run('cancel')}
					data-flx="boost.subscription-row.confirm-modal"
				/>
			)),
		);

	return (
		<section className={styles.row} aria-labelledby="boost-subscription-title" data-flx="boost.subscription-row">
			<span className={styles.gem}>
				<BoostGemIcon size={20} />
			</span>
			<div className={styles.text}>
				<h3 id="boost-subscription-title" className={styles.title}>
					{i18n._(TITLE_DESCRIPTOR)}
				</h3>
				<p className={styles.parts} data-flx="boost.subscription-row.summary">
					{parts.map((part, index) => (
						<span key={index} className={styles.part}>
							{part}
						</span>
					))}
				</p>
				{subscription.status === 'past_due' && (
					<p className={styles.warning} role="status">
						{i18n._(PAST_DUE_DESCRIPTOR)}
					</p>
				)}
				{subscription.status === 'needs_review' && (
					<p className={styles.summary} role="status">
						{i18n._(NEEDS_REVIEW_DESCRIPTOR)}
					</p>
				)}
				{subscription.status === 'pending' && (
					<p className={styles.summary} role="status">
						{i18n._(PAYMENT_PENDING_DESCRIPTOR)}
					</p>
				)}
			</div>
			<div className={styles.actions} data-flx="boost.subscription-row.actions">
				{subscription.can_change_quantity && (
					<Button
						small
						onClick={() => ModalCommands.push(modal(() => <BoostSubscriptionModal />))}
						data-flx="boost.subscription-row.button.manage"
					>
						{i18n._(MANAGE_DESCRIPTOR)}
					</Button>
				)}
				{subscription.can_update_payment_method && paymentUrl && (
					<a
						href={paymentUrl}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.paymentLink}
						onClick={() => {
							pageOpened.current = true;
							setPaymentUrl(null);
						}}
						data-flx="boost.subscription-row.payment-link"
					>
						{i18n._(OPEN_PAYMENT_PAGE_DESCRIPTOR)}
					</a>
				)}
				{subscription.can_update_payment_method && !paymentUrl && (
					<Button
						variant="secondary"
						small
						disabled={busy}
						onClick={() => void run('payment_method')}
						data-flx="boost.subscription-row.button.payment-method"
					>
						{i18n._(PAYMENT_METHOD_DESCRIPTOR)}
					</Button>
				)}
				{subscription.can_reactivate && (
					<Button
						variant="secondary"
						small
						disabled={busy}
						onClick={() => void run('reactivate')}
						data-flx="boost.subscription-row.button.reactivate"
					>
						{i18n._(REACTIVATE_DESCRIPTOR)}
					</Button>
				)}
				{subscription.can_cancel && (
					<Button
						variant="secondary"
						small
						disabled={busy}
						onClick={confirmCancel}
						data-flx="boost.subscription-row.button.cancel"
					>
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
				)}
			</div>
		</section>
	);
});
